import { Injectable } from '@angular/core';
import { RouterService } from './router.service';
import { catchError, map, Observable, throwError } from 'rxjs';
import {
    AssignmentBillResult,
    AssignmentReport,
    AssignmentReportResponse,
} from '../types/reports';
import { CachedHttpService } from './cached-http.service';
import { ApiError } from '../models/error';
import { AppConfigService } from '../../app.config';
import { AppConfig } from '../models/appConfig';
import { Report, ReportFilter, ReportResponse } from '../types/report/Report';
import { HttpClient } from '@angular/common/http';
import { cloneDeep } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class ReportsService {
    private _report: Report;
    config: AppConfig;
    reportsUrl: string;

    constructor(
        private cachedHttp: CachedHttpService,
        private _http: HttpClient,
        private routes: RouterService,
        config: AppConfigService,
    ) {
        this.config = config.getConfig();
        this.reportsUrl = `${this.config.microServiceUrl}reports`;

        this._report = NewReport;
    }

    /**
     * Get Reports url
     */
    get url() {
        return this.routes.getApiUrl('reports');
    }

    /**
     * Get cached report
     */
    get report() {
        return this._report;
    }

    /**
     * Set cached report
     */
    set report(report: Report) {
        this._report = cloneDeep(report);
    }

    /**
     * Returns true if Report Id is default value
     */
    get isNewReport() {
        return this._report.id === '00000000-0000-0000-0000-000000000000';
    }

    /**
     * Get a new report
     * @returns Default Report
     */
    getNewReport() {
        return cloneDeep(NewReport);
    }

    /**
     * load user assignments for auth
     * @param userId
     * @param type
     * @param report
     * @returns
     */
    loadAssignment(
        userId: number,
        type: string,
        report: AssignmentReport,
    ): Observable<AssignmentBillResult[]> {
        const url = `${this.url}/render`;
        return this.cachedHttp
            .post<AssignmentReportResponse>(
                url,
                report,
                `${url}-${type}-${userId}`,
            )
            .pipe(map((response) => response.results[0].results));
    }

    /**
     * Get report by Id
     * @param id Guid of report
     * @returns report with id
     */
    getReport(id: string): Observable<Report> {
        const url = `${this.reportsUrl}/reports/${id}`;

        return this._http
            .get<Report>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Get reports by type
     * @param type type enum for reports
     * @returns reports of type
     */
    getByType(type: number): Observable<Report[]> {
        const url = `${this.reportsUrl}/reports/type/${type}`;

        return this._http
            .get<Report[]>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Execute saved report and return results
     * @param reset bypass caching and call api if true
     * @returns results for saved report
     */
    executeReport(
        report: Report,
        reset: boolean = false,
    ): Observable<ReportResponse> {
        this._report = report;

        const url = `${this.reportsUrl}/query/execute`;

        return this.cachedHttp
            .post<ReportResponse>(
                url,
                this._report,
                `${url}-${this._report.id}`,
                reset,
            )
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Delete report by id
     * @param id report id
     * @returns
     */
    deleteReport(id: string): Observable<void> {
        const url = `${this.reportsUrl}/reports/${id}`;

        return this._http
            .delete<void>(url)
            .pipe(catchError((error) => this.handleError(error)));
    }

    /**
     * Save as new report of type
     * @report Report to be updated
     * @param type
     * @returns created report
     */
    createReport(type: number): Observable<Report> {
        const url = `${this.reportsUrl}/reports/${type}`;

        return this._http.post<Report>(url, this._report).pipe(
            map((result) => (this._report = result)),
            catchError((error) => this.handleError(error)),
        );
    }

    /**
     * Update Report
     * @returns updated report
     */
    updateReport(): Observable<Report> {
        const url = `${this.reportsUrl}/reports/${this._report.id}`;

        return this._http.put<Report>(url, this._report).pipe(
            map((result) => (this._report = result)),
            catchError((error) => this.handleError(error)),
        );
    }

    private handleError(error: ApiError) {
        return throwError(() => new ApiError('An unexpected error occurred'));
    }
}

const NewReport: Report = <Report>{
    id: '00000000-0000-0000-0000-000000000000',
    title: 'Report',
    subtitle: '',

    request: {
        type: 'Bill',
        displays: [
            {
                name: 'Status',
                type: 'Status',
            },
        ],
        filters: {
            analysisFilters: <ReportFilter[]>[],
            billFilters: [
                {
                    name: 'Session',
                    type: 'Bill',
                    exclude: false,
                    values: ['Current'],
                },
            ],
            codeSectionFilters: <ReportFilter[]>[],
            keywordFilters: <ReportFilter[]>[],
            listFilters: <ReportFilter[]>[],
            trackingDataFilters: <ReportFilter[]>[],
        },
        pagination: {
            page: 0,
            pageSize: 10,
        },
        sort: {
            name: 'Measure',
            type: 'Sort',
            direction: 'Ascending',
        },
    },
};
